import React from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { Email as EmailIcon, LocalShipping as ShipmentIcon } from "@mui/icons-material";
import { TinyValueComponent } from "../../../internal/components/TinyValueComponent";
import type { InputItem } from "./types";

interface InputItemDisplayProps {
  input: InputItem;
  onOpenEmail?: (emailId: string, event: React.MouseEvent) => void;
  onOpenShipment?: (shipmentId: string, event: React.MouseEvent) => void;
  onOpenReminder?: (reminderId: string, event: React.MouseEvent) => void;
}

/**
 * Shared component that renders an InputItem showing email, shipment, and reminder IDs.
 * Used in both the test set detail page and the pipeline results table.
 */
export function InputItemDisplay({ input, onOpenEmail, onOpenShipment, onOpenReminder }: InputItemDisplayProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexWrap: "wrap",
      }}
    >
      {input.emailId && (
        <>
          {onOpenEmail ? (
            <IconButton
              size="small"
              onClick={(e) => onOpenEmail(input.emailId!, e)}
              title="View email"
              sx={{ p: 0.25 }}
            >
              <EmailIcon fontSize="small" color="primary" />
            </IconButton>
          ) : (
            <EmailIcon fontSize="small" color="primary" sx={{ mr: 0.25 }} />
          )}
          <TinyValueComponent value={input.emailId} clipboard cropText />
        </>
      )}
      {input.shipmentId && (
        <>
          {onOpenShipment ? (
            <IconButton
              size="small"
              onClick={(e) => onOpenShipment(input.shipmentId!, e)}
              title="View shipment timeline"
              sx={{ p: 0.25, ml: input.emailId ? 1 : 0 }}
            >
              <ShipmentIcon fontSize="small" color="primary" />
            </IconButton>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: input.emailId ? 1 : 0 }}
            >
              shipment:
            </Typography>
          )}
          <TinyValueComponent value={input.shipmentId} clipboard />
        </>
      )}
      {input.reminderId && (
        <Chip
          label={`reminder: ${input.reminderType || "?"}`}
          size="small"
          variant="outlined"
          clickable={!!onOpenReminder}
          onClick={onOpenReminder ? (e) => onOpenReminder(input.reminderId!, e as React.MouseEvent) : undefined}
          sx={{ ml: input.emailId || input.shipmentId ? 1 : 0 }}
        />
      )}
    </Box>
  );
}
